import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IFilterAngularComp } from 'ag-grid-angular';
import { IFilterParams, IDoesFilterPassParams } from 'ag-grid-community';
import { CustomFilterService } from 'src/app/services/custom-filter.service';

import { CommonService } from '../../services/common.service';
import { ReportsService } from '../../services/reports.service';

interface HighMedLowFilterModel {
  filter: string | number;
  filterTo?: number | string | null;
  filterType: string;
  type: string;
}

interface HiMedLoNaSelection {
  veryHighSelected: boolean;
  highSelected: boolean;
  mediumSelected: boolean;
  lowSelected: boolean;
  notApplicableSelected: boolean;
}

type GridFilterOperator =
  | 'equals'
  | 'greaterThan'
  | 'lessThan'
  | 'notEqual'
  | 'lessThanOrEqual'
  | 'greaterThanOrEqual'
  | 'inRange';

@Component({
  selector: 'custom-vrm-rating-filter',
  template: `
    <div class="padding-10" fxLayout="column" fxFlex>
      <mat-checkbox color="primary" class="padding-5" [(ngModel)]="veryHighSelected" (change)="selectionChanged()">
        {{ 'REPORTDATA.veryHigh' | translate }}
      </mat-checkbox>

      <mat-checkbox color="primary" class="padding-5" [(ngModel)]="highSelected" (change)="selectionChanged()">
        {{ 'REPORTDATA.high' | translate }}
      </mat-checkbox>

      <mat-checkbox color="primary" class="padding-5" [(ngModel)]="mediumSelected" (change)="selectionChanged()">
        {{ 'REPORTDATA.medium' | translate }}
      </mat-checkbox>

      <mat-checkbox color="primary" class="padding-5" [(ngModel)]="lowSelected" (change)="selectionChanged()">
        {{ 'REPORTDATA.low' | translate }}
      </mat-checkbox>

      <mat-checkbox color="primary" class="padding-5" [(ngModel)]="notApplicableSelected" (change)="selectionChanged()">
        {{ 'REPORTDATA.na' | translate }}
      </mat-checkbox>
    </div>
  `,
  styles: [
    `
      :host ::ng-deep label.mat-checkbox-layout {
        width: 100%;
      }
      :host ::ng-deep label .mat-checkbox-inner-container {
        margin-left: 0px;
      }
    `,
  ],
})
export class CustomStandardHighMedLowRatingComponent implements IFilterAngularComp {
  private params: IFilterParams;

  veryHighSelected: boolean = true;
  highSelected: boolean = true;
  mediumSelected: boolean = true;
  lowSelected: boolean = true;
  notApplicableSelected: boolean = true;

  private ratingColumns = ['indexingRating', 'defensiveDriving', 'profileDriver', 'rating'];
  private columnId: string;

  constructor(
    private cService: CommonService,
    private rptService: ReportsService,
    private activeRoute: ActivatedRoute,
    private customFilterService: CustomFilterService
  ) {
    this.activeRoute.queryParams.subscribe({
      next: (queryParams: unknown) => {
        const filterFoundInQuery = this.deselectFiltersInitially(queryParams);
        if (filterFoundInQuery) {
          setTimeout(() => {
            this.ratingColumns.forEach((columnName) => {
              const filterModel: HighMedLowFilterModel = this.rptService.gridState.filterModel[columnName];
              const filterValue = filterModel ? filterModel.filter : '';

              this.tickHiMedLowNaSelections(filterValue);
            });

            this.selectionChanged();
          }, 50);
        } else {
          this.deselectFiltersInitially(this.rptService.gridState.filterModel);
          this.initializeFilterSelection();
        }
      },
    });
  }

  agInit(params: IFilterParams) {
    this.params = params;

    if (this.params.colDef && this.params.colDef.colId) {
      this.columnId = this.params.colDef.colId;
    }
  }

  isFilterActive(): boolean {
    return (
      !this.highSelected ||
      !this.veryHighSelected ||
      !this.mediumSelected ||
      !this.lowSelected ||
      !this.notApplicableSelected
    );
  }

  private initializeFilterSelection() {
    const cachedFilterSelections = this.getCachedFilterSelections();

    if (cachedFilterSelections) {
      for (const key in cachedFilterSelections) {
        if (key) {
          this[key] = cachedFilterSelections[key];
        }
      }

      this.selectionChanged();
    }

    console.log('%c Initial HiMedLo Filter state', 'color: lightblue', cachedFilterSelections);
  }

  private allRatings(): Array<number> {
    const filterSelections = Object.values(this.cService.highMediumLowRatingValues).reverse();
    if (!filterSelections.includes(0)) {
      filterSelections.unshift(0);
    }

    return filterSelections;
  }

  private deselectFiltersInitially(genericFilterObject: unknown): boolean {
    let filterFound: boolean = false;

    for (let i = 0; i < this.ratingColumns.length; i++) {
      const ratingCol = this.ratingColumns[i];

      if (genericFilterObject[ratingCol]) {
        this.veryHighSelected = false;
        this.highSelected = false;
        this.mediumSelected = false;
        this.lowSelected = false;
        this.notApplicableSelected = false;

        filterFound = true;
        this.columnId = ratingCol;
        break;
      }
    }

    return filterFound;
  }

  private tickHiMedLowNaSelections(filterValue: number | string) {
    const rating = this.cService.highMediumLowRatingValues;

    if (filterValue) {
      if (!Number.isNaN(Number(filterValue))) {
        if (Number(filterValue) == rating.veryHigh) {
          this.veryHighSelected = true;
        }

        if (Number(filterValue) == rating.high) {
          this.highSelected = true;
        }

        if (Number(filterValue) == rating.medium) {
          this.mediumSelected = true;
        }

        if (Number(filterValue) == rating.low) {
          this.lowSelected = true;
        }

        if (Number(filterValue) < 1) {
          this.notApplicableSelected = true;
        }
      } else if (filterValue && typeof filterValue === 'string') {
        if (filterValue.toLowerCase() === 'very high') {
          this.veryHighSelected = true;
        }

        if (filterValue.toLowerCase() === 'high') {
          this.highSelected = true;
        }

        if (filterValue.toLowerCase() === 'medium') {
          this.mediumSelected = true;
        }

        if (filterValue.toLowerCase() === 'low') {
          this.lowSelected = true;
        }

        if (filterValue.toLowerCase() === 'na') {
          this.notApplicableSelected = true;
        }
      }
    }
  }

  private getCachedFilterSelections(): HiMedLoNaSelection {
    const cachedFilterSelections: string = localStorage.getItem(this.cService.localStorageKeys.gridCustomHighMedLow);

    return cachedFilterSelections ? JSON.parse(cachedFilterSelections) : null;
  }

  private cacheFilterSelections() {
    const selections = {
      veryHighSelected: this.veryHighSelected,
      highSelected: this.highSelected,
      mediumSelected: this.mediumSelected,
      lowSelected: this.lowSelected,
      notApplicableSelected: this.notApplicableSelected,
    };

    localStorage.setItem(this.cService.localStorageKeys.gridCustomHighMedLow, JSON.stringify(selections));
  }

  private adjustPersistedFilterBasedOnSelection() {
    const selection = {
      maxValue: 4,
      minValue: 0,
      allItems: 5,
      singleItem: 1,
      twoItems: 2,
      threeItems: 3,
      fourItems: 4,
    };

    const filterSelections = this.generateNumericSelection();

    if (filterSelections.length === selection.allItems) {
      delete this.rptService.gridState.filterModel[this.columnId];
    }

    if (!filterSelections.length) {
      this.rptService.gridState.filterModel[this.columnId] = this.generateCondition(selection.maxValue, 'greaterThan');
    }

    if (filterSelections.length === selection.singleItem) {
      this.rptService.gridState.filterModel[this.columnId] = this.generateCondition(filterSelections[0]);
    }

    if (filterSelections.length === selection.twoItems) {
      this.rptService.gridState.filterModel[this.columnId] = {
        condition1: this.generateCondition(filterSelections[0]),
        condition2: this.generateCondition(filterSelections[1]),

        filterType: 'number',
        operator: 'OR',
      };
    }

    if (filterSelections.length === selection.threeItems) {
      const unselected = this.getMissingSelections(filterSelections);

      this.rptService.gridState.filterModel[this.columnId] = {
        condition1: this.generateCondition(unselected[0], 'notEqual'),
        condition2: this.generateCondition(unselected[1], 'notEqual'),

        filterType: 'number',
        operator: 'AND',
      };
    }

    if (filterSelections.length === selection.fourItems) {
      const unselected = this.getMissingSelections(filterSelections);

      this.rptService.gridState.filterModel[this.columnId] = this.generateCondition(unselected[0], 'notEqual');
    }

    console.log('%c Filter model changes to PERSIST', 'color: lightblue', this.rptService.gridState.filterModel);

    this.rptService.cacheFiltersAcrossClientInstances();
  }

  private generateNumericSelection(): Array<number> {
    const numericSelection = [];

    if (this.veryHighSelected) {
      numericSelection.push(this.cService.highMediumLowRatingValues.veryHigh);
    }

    if (this.highSelected) {
      numericSelection.push(this.cService.highMediumLowRatingValues.high);
    }

    if (this.mediumSelected) {
      numericSelection.push(this.cService.highMediumLowRatingValues.medium);
    }

    if (this.lowSelected) {
      numericSelection.push(this.cService.highMediumLowRatingValues.low);
    }

    if (this.notApplicableSelected) {
      numericSelection.push(0);
    }

    return numericSelection.sort();
  }

  private generateCondition(
    filterValue: number | string,
    nonEqualsOperator: GridFilterOperator = 'equals',
    filterToValue: number | string = null,
    filterStrongType: string = 'number'
  ): HighMedLowFilterModel {
    return {
      filter: filterValue,
      filterTo: filterToValue,
      filterType: filterStrongType,
      type: nonEqualsOperator,
    };
  }

  private getMissingSelections(filterSelections: Array<number>): Array<number> {
    const missingSelections: Array<number> = [];
    const allPossibleSelections = this.allRatings();

    allPossibleSelections.forEach((selection) => {
      if (filterSelections.includes(selection)) {
        missingSelections.push(selection);
      }
    });

    return missingSelections;
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    let filterExpression: boolean;
    const cellValue = this.customFilterService.getCellValue(this.params, params);
    const rating = this.cService.highMediumLowRatingValues;

    // Single de-selections
    if (!this.veryHighSelected) {
      filterExpression = cellValue < rating.veryHigh;
    }

    if (!this.highSelected) {
      filterExpression = cellValue != rating.high;
    }

    if (!this.mediumSelected) {
      filterExpression = cellValue != rating.medium;
    }

    if (!this.lowSelected) {
      filterExpression = cellValue != rating.low;
    }

    if (!this.notApplicableSelected) {
      filterExpression = cellValue >= rating.low;
    }

    // Double de-selections
    if (!this.veryHighSelected && !this.highSelected) {
      filterExpression = cellValue <= rating.medium;
    }

    if (!this.veryHighSelected && !this.mediumSelected) {
      filterExpression = cellValue == rating.high || cellValue <= rating.low;
    }

    if (!this.veryHighSelected && !this.lowSelected) {
      filterExpression = cellValue == rating.high || cellValue == rating.medium || cellValue < rating.low;
    }

    if (!this.veryHighSelected && !this.notApplicableSelected) {
      filterExpression = cellValue == rating.high || cellValue == rating.medium || cellValue == rating.low;
    }

    if (!this.highSelected && !this.mediumSelected) {
      filterExpression = cellValue == rating.veryHigh || cellValue <= rating.low;
    }

    if (!this.highSelected && !this.lowSelected) {
      filterExpression = cellValue == rating.veryHigh || cellValue == rating.medium || cellValue < rating.low; // <-- Captures NA
    }

    if (!this.highSelected && !this.notApplicableSelected) {
      filterExpression = cellValue == rating.veryHigh || cellValue == rating.medium || cellValue == rating.low;
    }

    if (!this.mediumSelected && !this.lowSelected) {
      filterExpression = cellValue == rating.veryHigh || cellValue == rating.high || cellValue < rating.low; // <-- Less than low = NA
    }

    if (!this.mediumSelected && !this.notApplicableSelected) {
      filterExpression = cellValue == rating.veryHigh || cellValue == rating.high || cellValue == rating.low;
    }

    if (!this.lowSelected && !this.notApplicableSelected) {
      filterExpression = cellValue >= rating.medium;
    }

    // Triple de-selections
    if (!this.veryHighSelected && !this.highSelected && !this.mediumSelected) {
      filterExpression = cellValue <= rating.low;
    }

    if (!this.veryHighSelected && !this.mediumSelected && !this.lowSelected) {
      filterExpression = cellValue == rating.high || cellValue < rating.low;
    }

    if (!this.veryHighSelected && !this.lowSelected && !this.notApplicableSelected) {
      filterExpression = cellValue == rating.high || cellValue == rating.medium;
    }

    if (!this.highSelected && !this.mediumSelected && !this.lowSelected) {
      filterExpression = cellValue == rating.veryHigh || cellValue < rating.low; // <-- Not applicable selected
    }

    if (!this.highSelected && !this.mediumSelected && !this.notApplicableSelected) {
      filterExpression = cellValue == rating.veryHigh || cellValue == rating.low;
    }

    if (!this.highSelected && !this.lowSelected && !this.notApplicableSelected) {
      filterExpression = cellValue == rating.veryHigh || cellValue == rating.medium;
    }

    if (!this.mediumSelected && !this.lowSelected && !this.notApplicableSelected) {
      filterExpression = cellValue == rating.veryHigh || cellValue == rating.high;
    }

    // Quad deselection / single selection
    if (!this.veryHighSelected && !this.highSelected && !this.mediumSelected && !this.lowSelected) {
      filterExpression = cellValue < rating.low; // NA
    }

    if (!this.veryHighSelected && !this.highSelected && !this.mediumSelected && !this.notApplicableSelected) {
      filterExpression = cellValue == rating.low;
    }

    if (!this.veryHighSelected && !this.highSelected && !this.lowSelected && !this.notApplicableSelected) {
      filterExpression = cellValue == rating.medium;
    }

    if (!this.veryHighSelected && !this.mediumSelected && !this.lowSelected && !this.notApplicableSelected) {
      filterExpression = cellValue == rating.high;
    }

    if (!this.highSelected && !this.mediumSelected && !this.lowSelected && !this.notApplicableSelected) {
      filterExpression = cellValue > rating.high;
    }

    // Catch all - nothing selected
    if (
      !this.veryHighSelected &&
      !this.highSelected &&
      !this.mediumSelected &&
      !this.lowSelected &&
      !this.notApplicableSelected
    ) {
      filterExpression = cellValue == null;
    }

    return filterExpression;
  }

  getModel(): any {
    return;
  }

  setModel(model: any) {}

  // ----------------------

  selectionChanged() {
    setTimeout(() => {
      this.params.filterChangedCallback();

      this.adjustPersistedFilterBasedOnSelection();
      this.cacheFilterSelections();

      console.log('%c SELECTED Filter Values', 'color: lime', {
        columnId: this.columnId,
        selections: {
          veryHigh: this.veryHighSelected,
          high: this.highSelected,
          medium: this.mediumSelected,
          low: this.lowSelected,
          na: this.notApplicableSelected,
        },
      });
    }, 50);
  }
}
